<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-account-group" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [(ngModel)]="filterString" (ngModelChange)="applyFilter($event)" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="applyFilter('')" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<mat-menu #addMenu="matMenu" [overlapTrigger]="false">
  <button mat-menu-item (click)="addOrEditTeam(null)" title="Add a new team">New Team</button>
  <button mat-menu-item (click)="addTeamFromUnit()" title="Add team from unit">
    Add team from unit
  </button>
</mat-menu>

@if (msel) {
@if (casterTeamVariablesError) {
<div class="topology-warning">Topology teams: {{ casterTeamVariablesError }}</div>
}
@for (warning of duplicateTeamVariableWarnings; track warning) {
<div class="topology-warning">{{ warning }}</div>
}
@for (variable of unmappedTopologyTeams; track variable) {
<div class="topology-warning">
  Topology team "{{ variable }}" has no MSEL team. {{ getUnmappedTopologyTeamDestination(variable) }}
</div>
}
@for (team of staleMappings; track team.id) {
<div class="topology-warning">
  {{ team.name }} is mapped to topology team "{{ team.casterTeamVariable }}", which the Caster template no longer declares.
</div>
}
<div class="frame">
  <div class="table-container">
    <table mat-table #teamTable [dataSource]="teamDataSource" matSort (matSortChange)="sortChanged($event)" multiTemplateDataRows>
      <!-- action column -->
      <ng-container matColumnDef="action">
        <th mat-header-cell *matHeaderCellDef class="column-action">
          @if (canEditMsel()) {
          <button mat-icon-button [matMenuTriggerFor]="addMenu" (menuOpened)="refreshUnits()" title="Add a team" style="outline: none;">
            <mat-icon class="mdi-24px center-self" fontIcon="mdi-plus-circle"></mat-icon>
          </button>
          }
        </th>
        <td mat-cell *matCellDef="let element" class="column-action">
          @if (canEditMsel()) {
          <button mat-icon-button (click)="addOrEditTeam(element); $event.stopPropagation();"
            title="Edit {{ element.name }}">
            <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline" title="Edit {{ element.name }}"></mat-icon>
          </button>
          }
          @if (canEditMsel()) {
          <button mat-icon-button (click)="deleteTeam(element); $event.stopPropagation();" title="Delete team">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline" title="Delete {{ element.name }}"></mat-icon>
          </button>
          }
        </td>
      </ng-container>
      <!-- shortName column -->
      <ng-container matColumnDef="shortName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-short-name">Short Name</th>
        <td mat-cell *matCellDef="let element" class="column-short-name">
          {{ element.shortName }}
        </td>
      </ng-container>
      <!-- name column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-name">Name</th>
        <td mat-cell *matCellDef="let element" class="column-name">
          {{ element.name }}
        </td>
      </ng-container>
      <!-- email column -->
      <ng-container matColumnDef="email">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-email">Email</th>
        <td mat-cell *matCellDef="let element" class="column-email">{{ element.email }}</td>
      </ng-container>
      <!-- teamType column -->
      <ng-container matColumnDef="teamType">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="column-team-type"
          matTooltip="The CITE team type this team is created with when the MSEL is pushed to CITE">Team Type</th>
        <td mat-cell *matCellDef="let element" class="column-team-type">
          {{ getCiteTeamTypeName(element.citeTeamTypeId) }}
        </td>
      </ng-container>
      <!-- topologyTeam column -->
      <ng-container matColumnDef="topologyTeam">
        <th mat-header-cell *matHeaderCellDef class="column-topology-team"
          matTooltip="The Caster template variable this team's Player team ID is written to">Topology Team</th>
        <td mat-cell *matCellDef="let element" class="column-topology-team">
          {{ getTeamVariable(element) }}
          @if (getMappedOverTeam(element); as mappedOver) {
          <span class="topology-unused">(replaced by {{ mappedOver.name }})</span>
          } @else if (!element.casterTeamVariable && casterTeamVariables && !casterTeamVariables.includes(getTeamVariable(element))) {
          <span class="topology-unused">(not in template)</span>
          }
        </td>
      </ng-container>
      <!-- invitation column -->
      <ng-container matColumnDef="invitation">
        <th mat-header-cell *matHeaderCellDef class="column-invitation"
          matTooltip="Any member of this team can invite others to it">Open Invite</th>
        <td mat-cell *matCellDef="let element" class="column-invitation">
          <mat-checkbox [checked]="element.canTeamMemberInvite" disabled
            (click)="$event.stopPropagation();" matTooltip="Any team member can invite"></mat-checkbox>
        </td>
      </ng-container>
      <!-- Expanded Content Column -->
      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
          <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
            @if (expandedElementId === element.id) {
            <div class="expanded-detail-div">
              <app-team-users [team]="element"></app-team-users>
            </div>
            }
          </div>
        </td>
      </ng-container>
      <!-- row definitions -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class]="getRowClass(row.id)"
        (click)="rowClicked(row)"></tr>
      <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
    </table>
  </div>
</div>
}
